/**
 * Cart Store
 * 
 * Pure state management using Zustand.
 * This file defines the store structure and wires up actions.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { CartStore, CartState, CartStateMutations } from './types';
import { createCartActions } from './actions';
import { STORAGE_KEYS } from '@/utils/constants';

/**
 * Initial cart state with discriminated union
 */
const initialState: CartState = {
  items: [],
  cartId: null,
  checkoutUrl: null,
  checkoutState: { status: 'idle' },
};

/**
 * Create the cart store with Zustand
 * 
 * Uses persist middleware to save cart to localStorage,
 * so cart persists across page reloads.
 */
export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => {
      // State mutation functions
      const mutations: CartStateMutations = {
        setItems: (items: CartState['items']) => set({ items }),
        setCartId: (cartId: CartState['cartId']) => set({ cartId }),
        setCheckoutUrl: (checkoutUrl: CartState['checkoutUrl']) => set({ checkoutUrl }),
        setCheckoutLoading: () => set({ checkoutState: { status: 'loading' } }),
        setCheckoutSuccess: () => set({ checkoutState: { status: 'success', data: undefined } }),
        setCheckoutError: (error) => set({ checkoutState: { status: 'error', error } }),
        resetCheckoutState: () => set({ checkoutState: { status: 'idle' } }),
      };

      // Create actions using mutations
      const actions = createCartActions(get, mutations);

      // Return complete store
      return {
        ...initialState,
        ...mutations,
        ...actions,
      };
    },
    {
      name: STORAGE_KEYS.cart,
      storage: createJSONStorage(() => localStorage),
      
      // Only persist state, not functions
      partialize: (state) => ({
        items: state.items,
        cartId: state.cartId,
        checkoutUrl: state.checkoutUrl,
      }),
    }
  )
);

/**
 * Selector hooks for common use cases
 * These provide convenience methods for components
 */

// Get just the items
export const useCartItems = () => useCartStore(state => state.items);

// Get checkout state (loading/error)
export const useCartCheckoutState = () => useCartStore(state => state.checkoutState);

// Get just the actions
export const useCartActions = () => useCartStore(state => ({
  addItem: state.addItem,
  updateQuantity: state.updateQuantity,
  removeItem: state.removeItem,
  clearCart: state.clearCart,
  createCheckout: state.createCheckout,
}));
